// Auto-creation of benefit-usage "invoices" when a Mthunzi (OMM) claim is
// approved. Every benefit carried on the claim (the primary the agent picked at
// initiation plus the bundled add-ons) becomes one benefit usage. All usages on
// a claim share a single claim-level invoice number and each carries its own
// expense code. Format, codes and amounts follow the v5 claims-journey spec.
//
// Backend contract (claims-administration-dcp):
//   POST /api/claims/complex/benefit_usages?claim_guid=&partner=
//   body: { benefit_type, benefit_amount_in_cents, benefit_usage_count,
//           invoice_number, additional_details:{ expense_code, ... } }
// The backend persists invoice_number / additional_details verbatim (it does not
// generate them), so we build both here.

// Benefit type -> expense-code prefix (the "what": FS = funeral service, etc.).
const EXPENSE_CODE_PREFIX = {
  "Funeral Services": "FS",
  "Funeral Service": "FS",
  "Cash in Lieu of Service": "CS",
  "Muslim Funeral": "MF",
  "Grocery Allowance": "GA",
  "Bus Cash Benefit": "BC",
  "Hospital Cash Benefit": "HC",
};

// Package tier -> expense-code suffix (the "which package").
const PACKAGE_SUFFIX = { lite: "001", standard: "002", premier: "003" };

// Fixed per-package Grocery amounts (in cents) used only to infer the package
// tier when the policy does not expose it. From the v5 PACKAGE_CONFIG
// (150k / 200k / 250k).
const GROCERY_CENTS_TO_TIER = {
  15000000: "lite",
  20000000: "standard",
  25000000: "premier",
};

// Resolve the package tier: prefer an explicit package on the policy, else infer
// from the Grocery Allowance amount on the claim, else default to standard.
// getPolicyByGuidDcp returns the policy_administration_complex row directly, which
// exposes the tier as a flat `product_name` string and as `additional_details.tier`
// (stored "lite"/"standard"/"premium" - note "premium", not "premier" - per the
// underwriter's own tier_labels table) - not the package_code/package/product.name
// shape this used to check for, which this policy never had.
export const getPackageTier = (policy, benefits) => {
  const raw =
    policy &&
    (policy.package_code ||
      policy.package ||
      (policy.additional_details && policy.additional_details.tier) ||
      policy.product_name ||
      (policy.product && policy.product.name));
  if (raw) {
    const value = ("" + raw).toLowerCase();
    if (value.indexOf("lite") !== -1) return "lite";
    if (value.indexOf("premier") !== -1 || value.indexOf("premium") !== -1)
      return "premier";
    if (value.indexOf("standard") !== -1) return "standard";
  }

  const grocery = (benefits || []).find(function (b) {
    return b.type === "Grocery Allowance";
  });
  if (grocery && GROCERY_CENTS_TO_TIER[grocery.max_benefit_amount_in_cents]) {
    return GROCERY_CENTS_TO_TIER[grocery.max_benefit_amount_in_cents];
  }

  return "standard";
};

export const getExpenseCode = (benefitType, tier) => {
  const prefix = EXPENSE_CODE_PREFIX[benefitType];
  if (!prefix) return null;
  return prefix + (PACKAGE_SUFFIX[tier] || PACKAGE_SUFFIX.standard);
};

// Single claim-level invoice number: INV-CLM-<5-digit random><last-4 of ts>.
// Random per the spec and effectively unique per claim (generated once, then
// shared across all of this claim's benefit-usage lines).
export const generateClaimInvoiceNumber = () => {
  const rand = Math.floor(Math.random() * 90000 + 10000);
  const ts = ("" + Date.now()).slice(-4);
  return "INV-CLM-" + rand + ts;
};

export const buildManualInvoiceFields = (claim, benefitType, policy) => {
  const benefits = (claim && claim.benefits) || [];
  const usages = (claim && claim.benefit_usages) || [];
  const tier = getPackageTier(policy, benefits);
  const existing = usages.find(function (usage) {
    return usage.invoice_number;
  });
  return {
    invoice_number: existing
      ? existing.invoice_number
      : generateClaimInvoiceNumber(),
    expense_code: getExpenseCode(benefitType, tier),
    package: tier,
  };
};

// number_of_days_in_hospital is never actually forwarded by the underwriter (see the
// same gap documented in ClaimDetails.js) - the real, server-computed key is
// days_admitted. Without this fallback, any claim whose paid_days isn't set yet
// (submitted before that field existed on the flow) silently invoices for 0 days,
// even though the claim's real approved amount is non-zero - the day count just
// couldn't be found at all, not because it was legitimately 0.
const getDaysInHospital = (claim) => {
  const details =
    (claim.claim_details && claim.claim_details.additional_details) || {};
  return (
    Number(details.days_admitted || details.number_of_days_in_hospital) || 0
  );
};

// The underwriter engine (old_mutual_malawi_mthunzi_hcp_claim.yaml) already computes the true
// per-day rate as effective_daily_rate and carries it through as
// claim_details.additional_details.daily_rate_applied. Claims submitted before that engine migration
// never had this field, so callers must still fall back to re-deriving a rate for those.
const getDailyRateAppliedInCents = (claim) => {
  const details =
    (claim.claim_details && claim.claim_details.additional_details) || {};
  return Number(details.daily_rate_applied) || 0;
};

// Days actually payable for this event - days_admitted, unless the per-event cap bit, in which case
// whatever the cap works out to in days. The per-event cap limits payout for a single admission, not
// entitlement: days beyond it aren't paid now, but aren't forfeited either - they stay part of the
// dependant's 50-day annual bucket for a later claim. The underwriter computes this as paid_days
// (claim_details.additional_details.paid_days), already net of the 2-day non-payable excess and
// already capped at the per-event limit - trust it as-is. Claims submitted before that field existed
// fall back to the raw day count and still need the excess subtracted here, since nothing upstream
// ever did for them.
const HOSPITAL_EXCESS_DAYS = 2;

const getPaidDays = (claim) => {
  const details =
    (claim.claim_details && claim.claim_details.additional_details) || {};
  const paidDays = Number(details.paid_days);
  if (paidDays) return paidDays;
  return Math.max(0, getDaysInHospital(claim) - HOSPITAL_EXCESS_DAYS);
};

// Amount (in cents) seeded on the benefit usage. All lines start at the full
// cover amount (editable afterwards via the Edit modal); Hospital Cash is
// paid-days x daily-rate, capped at the max per event.
//
// The daily rate must come from the underwriter's own daily_rate_applied, not from re-dividing
// max_benefit_amount_in_cents by max_benefit_usage_count: max_benefit_amount_in_cents is the payout
// already capped at the per-event limit - dividing the capped total by max_benefit_usage_count
// understates the true daily rate whenever capping occurred.
const getBenefitAmountInCents = (benefit, claim) => {
  const max = benefit.max_benefit_amount_in_cents || 0;
  if (benefit.type === "Hospital Cash Benefit") {
    const usageMax = benefit.max_benefit_usage_count || 0;
    const daily =
      getDailyRateAppliedInCents(claim) || (usageMax ? max / usageMax : 0);
    return Math.min(getPaidDays(claim) * daily, max);
  }
  return max;
};

const getUsageCount = (benefit, claim) => {
  if (benefit.type === "Hospital Cash Benefit") {
    return getPaidDays(claim);
  }
  return 1;
};

// Build one create-benefit-usage payload per claim benefit that does not already
// have a usage (idempotent), all sharing one freshly generated invoice number.
export const buildBenefitUsagePayloads = (claim, policy) => {
  const benefits = (claim && claim.benefits) || [];
  const existing = (claim && claim.benefit_usages) || [];
  const tier = getPackageTier(policy, benefits);
  const invoiceNumber = generateClaimInvoiceNumber();
  const existingTypes = existing.map(function (u) {
    return u.benefit_type;
  });

  return benefits
    .filter(function (b) {
      return EXPENSE_CODE_PREFIX[b.type];
    })
    .filter(function (b) {
      return existingTypes.indexOf(b.type) === -1;
    })
    .map(function (b) {
      return {
        claim_guid: claim.guid,
        benefit_type: b.type,
        benefit_amount_in_cents: Math.floor(getBenefitAmountInCents(b, claim)),
        benefit_usage_count: getUsageCount(b, claim),
        invoice_number: invoiceNumber,
        additional_details: {
          expense_code: getExpenseCode(b.type, tier),
          package: tier,
          auto_generated: true,
        },
      };
    });
};
